import { inject, signal, computed, Directive, OnInit, OnDestroy } from "@angular/core";
import {
  AllowedCurrencies,
  CurrencyType,
} from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { WidgetMenuType } from "@bitwarden/web-vault/app/models/types/widget.types";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";
import {
  PluginConfigService,
  PluginIdentifier,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { DatePipe } from "@angular/common";
import { debounceTime, Subject } from "rxjs";

const MANUALLY_ENTERED_PRICE = "manuallyEnterPrice";
const FIND_PRICE = "findPrice";

@Directive()
export class SymbolFormDirective implements OnInit, OnDestroy {
  protected datePipe: DatePipe = inject(DatePipe);
  private searchInputSubject: Subject<string> = new Subject<string>();
  private dateInput = signal<string>("");
  protected symbolInput = signal<string>("");
  protected userStoreService = inject(UserStoreService);
  protected dataPluginStoreService = inject(DataPluginStoreService);
  private pluginConfigService = inject(PluginConfigService);
  protected readonly AllowedCurrencies = AllowedCurrencies;
  protected selectedPlugin = {
    name: "EODHD",
  };

  protected defaultSystemCurrency: CurrencyType =
    this.userStoreService.preferences.preferenceView().coreCurrency;

  protected isEodhdInstalled = computed(() => {
    return !!this.pluginConfigService.getConfig(PluginIdentifier.DATABANK);
  });

  protected menu = computed(() => ({
    type: "text" as WidgetMenuType,
    defaultSelected: MANUALLY_ENTERED_PRICE,
    options: this.isEodhdInstalled()
      ? [
          { displayValue: "Find Price", value: FIND_PRICE },
          { displayValue: "Manually Enter Price", value: MANUALLY_ENTERED_PRICE },
        ]
      : [{ displayValue: "Manually Enter Price", value: MANUALLY_ENTERED_PRICE }],
  }));

  protected searchedSymbol = computed(() => {
    return this.dataPluginStoreService.databank.references().find((ref) => {
      return ref.date.date == this.dateInput() && ref.base == this.symbolInput();
    });
  });

  protected currentTab = signal<string>(MANUALLY_ENTERED_PRICE);
  protected isFindPrice = computed(() => {
    return this.currentTab() === FIND_PRICE && this.isEodhdInstalled();
  });

  // EODHD's Find Price is available on the Free tier — no longer gated on subscription.
  protected isFreeSubscription = computed(() => {
    return false;
  });

  protected enableFindPrice = computed(() => {
    return this.isFindPrice();
  });

  protected defaultCurrency = computed(() => {
    return this.isFindPrice() ? this.searchedSymbol()?.symbol : this.defaultSystemCurrency;
  });

  ngOnInit() {
    this.searchInputSubject
      .pipe(debounceTime(300))
      .subscribe((symbol) => this.fetchReferenceData(symbol));
  }

  fetchReferenceData(symbol: string) {
    this.dataPluginStoreService.databank.fetch({
      type: "references",
      limit: 20,
      symbol: symbol,
      date: this.dateInput() || this.datePipe.transform(Date.now(), "YYYY-MM-dd"),
      reliableTimeSource: true,
    });
  }

  onSearchInputChange(symbol: string, date: string) {
    if (symbol.trim() !== "") {
      this.dateInput.set(date);
      this.symbolInput.set(symbol);
      this.searchInputSubject.next(symbol);
    }
  }

  ngOnDestroy() {
    this.searchInputSubject.complete();
  }
}
